import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { BarChart as AnalyticsIcon, ExpandMore } from '@mui/icons-material';
import api from '../../config/api';
import { brandService } from '../../services/brandService';

const GoogleAnalyticsSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  
  // Multi-brand support
  const [brands, setBrands] = useState([]);
  const [propertyBrandMapping, setPropertyBrandMapping] = useState({});

  // Parse URL parameters
  const urlParams = new URLSearchParams(location.search);
  const tempId = urlParams.get('temp_id');
  const brandId = urlParams.get('brand_id');

  useEffect(() => {
    if (!tempId) {
      setError('Missing temporary ID. Please restart the OAuth process.');
      setLoading(false);
      return;
    }

    fetchProperties();
    fetchBrands();
  }, [tempId]);

  const fetchBrands = async () => {
    try {
      const brandsData = await brandService.getBrands();
      setBrands(brandsData || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await api.get('/api/connectors/google/analytics/properties', {
        params: { temp_id: tempId }
      });

      if (response.data.success) {
        setAccounts(response.data.accounts);
        setProperties(response.data.properties);
      } else {
        setError(response.data.message || 'Failed to fetch properties');
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError(err.response?.data?.message || 'Failed to fetch Analytics properties');
    } finally {
      setLoading(false);
    }
  };

  const handlePropertySelection = (propertyId, checked) => {
    if (checked) {
      const property = properties.find(p => p.propertyId === propertyId);
      setSelectedProperties([...selectedProperties, property]);
      // For already-connected properties, default to their existing brand (re-auth flow).
      // Otherwise use the brand from the OAuth URL params.
      const defaultBrand = property?.alreadyConnected && property?.connectedBrandId
        ? String(property.connectedBrandId)
        : brandId;
      setPropertyBrandMapping(prev => ({
        ...prev,
        [propertyId]: defaultBrand
      }));
    } else {
      setSelectedProperties(selectedProperties.filter(p => p.propertyId !== propertyId));
      // Remove brand mapping
      setPropertyBrandMapping(prev => {
        const newMapping = { ...prev };
        delete newMapping[propertyId];
        return newMapping;
      });
    }
  };

  const handleBrandChange = (propertyId, newBrandId) => {
    setPropertyBrandMapping(prev => ({
      ...prev,
      [propertyId]: newBrandId
    }));
  };

  const handleCreateConnectors = async () => {
    if (selectedProperties.length === 0) {
      setError('Please select at least one property to connect');
      return;
    }

    // Validate brands
    const propertiesWithoutBrands = selectedProperties.filter(property => 
      !propertyBrandMapping[property.propertyId]
    );
    if (propertiesWithoutBrands.length > 0) {
      setError(`Please select a brand for all selected properties`);
      return;
    }

    setCreating(true);
    setError('');

    try {
      const createdConnectors = [];

      for (const property of selectedProperties) {
        const selectedBrandId = propertyBrandMapping[property.propertyId] || brandId;
        const response = await api.post('/api/connectors/google/analytics/create', {
          temp_id: tempId,
          brand_id: parseInt(selectedBrandId),
          property_id: property.propertyId,
          property_name: property.propertyName,
          property_display_name: property.displayName,
          account_name: property.accountName
        });

        if (response.data.success) {
          createdConnectors.push(response.data.connector);
        } else {
          throw new Error(`Failed to create connector for ${property.displayName}: ${response.data.message}`);
        }
      }

      // Get unique brand names
      const uniqueBrands = [...new Set(selectedProperties.map(property => 
        getBrandName(propertyBrandMapping[property.propertyId] || brandId)
      ))];

      // Redirect back to connectors page with success message
      navigate('/connectors', { 
        state: { 
          message: `Successfully created ${createdConnectors.length} Google Analytics connector(s) across ${uniqueBrands.length} brand(s): ${uniqueBrands.join(', ')}`,
          type: 'success'
        }
      });

    } catch (err) {
      console.error('Error creating connectors:', err);
      setError(err.message || 'Failed to create connectors');
    } finally {
      setCreating(false);
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => parseInt(b.id) === parseInt(brandId));
    return brand ? brand.name : 'Unknown Brand';
  };

  // Group properties by account
  const propertiesByAccount = properties.reduce((acc, property) => {
    const accountName = property.accountName || 'Unknown Account';
    if (!acc[accountName]) {
      acc[accountName] = [];
    }
    acc[accountName].push(property);
    return acc;
  }, {});

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Analytics properties...</Typography>
      </Box>
    );
  }

  if (error && properties.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/connectors')}>
          Back to Connectors
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <AnalyticsIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Select Google Analytics Properties
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Choose one or more GA4 properties to connect. You can assign each property to a different brand.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {properties.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No Analytics properties found. Make sure you have GA4 properties in your Google Analytics account.
        </Alert>
      ) : (
        <>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Available Properties ({properties.length})
          </Typography>

          {Object.entries(propertiesByAccount).map(([accountName, accountProperties]) => (
            <Accordion key={accountName} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">
                  {accountName} ({accountProperties.length} properties)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {accountProperties.map((property) => (
                    <Grid item xs={12} md={6} key={property.propertyId}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedProperties.some(p => p.propertyId === property.propertyId)}
                                onChange={(e) => handlePropertySelection(property.propertyId, e.target.checked)}
                                disabled={creating}
                              />
                            }
                            label={
                              <Box sx={{ width: '100%' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {property.displayName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  ID: {property.propertyId}
                                </Typography>
                                {property.websiteUrl && (
                                  <Typography variant="body2" color="text.secondary">
                                    {property.websiteUrl}
                                  </Typography>
                                )}
                                
                                <Divider sx={{ my: 1 }} />

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                  <Chip
                                    label="GA4 Property"
                                    size="small"
                                    color="primary"
                                  />
                                  {property.alreadyConnected && (
                                    <Chip
                                      label={property.connectedBrandName
                                        ? `Connected · ${property.connectedBrandName}`
                                        : 'Already connected'}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>

                                {/* Brand Selection */}
                                {selectedProperties.some(p => p.propertyId === property.propertyId) && (
                                  <Box sx={{ mt: 2 }}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel id={`brand-select-${property.propertyId}`}>Assign to Brand</InputLabel>
                                      <Select
                                        labelId={`brand-select-${property.propertyId}`}
                                        value={propertyBrandMapping[property.propertyId] || ''}
                                        label="Assign to Brand"
                                        onChange={(e) => handleBrandChange(property.propertyId, e.target.value)}
                                        disabled={creating}
                                        sx={{ borderRadius: 1 }}
                                      >
                                        {brands.map(brand => (
                                          <MenuItem key={brand.id} value={brand.id}>
                                            {brand.name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {selectedProperties.length} property(ies) selected
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/connectors')}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateConnectors}
                disabled={selectedProperties.length === 0 || creating}
                startIcon={creating ? <CircularProgress size={20} /> : null}
              >
                {creating ? 'Creating...' : `Create ${selectedProperties.length} Connector(s)`}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default GoogleAnalyticsSelection;